import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

const ExitPayloadModal = ({
    isOpen,
    onClose,
    positions = [],
    onConfirm,
    onRemove,
    isLoading
}) => {
    const [qtyOverrides, setQtyOverrides] = useState({});
    const [typeOverrides, setTypeOverrides] = useState({});
    const [percentage, setPercentage] = useState("");

    const LOT_SIZES = {
        'BANKNIFTY': 30,
        'FINNIFTY': 60,
        'NIFTY': 65,
        'SENSEX': 20,
        'MIDCAP': 120
    };

    // Reset overrides when modal opens or positions change drastically
    useEffect(() => {
        if (isOpen) {
            setQtyOverrides({});
            setTypeOverrides({});
        }
    }, [isOpen, positions.length]);

    if (!isOpen) return null;

    // Helper to generate unique key for a position
    const getRowKey = (p) => {
        // Check various ID/key properties to be safe across different views
        const cid = p.client_id || p.clientId;
        const sym = p.symbol;
        const strat = p.strategy || "NO_STRAT";
        return `${cid}|${sym}|${strat}`;
    };

    const getClientData = (p) => ({
        clientId: p.client_id || p.clientId,
        symbol: p.symbol,
        strategy: p.strategy,
        side: p.side,
        qty: Math.abs(p.net_qty ?? p.qty ?? p.netQty ?? 0)
    });

    const getDisplayQty = (p) => {
        const key = getRowKey(p);
        if (qtyOverrides[key] !== undefined) return qtyOverrides[key];
        return getClientData(p).qty;
    };

    const getDisplayType = (p) => {
        const key = getRowKey(p);
        if (typeOverrides[key] !== undefined) return typeOverrides[key];
        // Default to 1 (LIMIT) as requested, fallback to p.type if valid, or 1
        return p.type || 1;
    };

    const handleQtyChange = (p, newVal) => {
        const key = getRowKey(p);
        setQtyOverrides(prev => ({
            ...prev,
            [key]: newVal
        }));
    };

    const handleTypeChange = (p, newVal) => {
        const key = getRowKey(p);
        setTypeOverrides(prev => ({
            ...prev,
            [key]: parseInt(newVal, 10) // Store as number
        }));
    };

    const normalizeSide = (s) => {
        if (typeof s === 'string') {
            if (s.toLowerCase().startsWith('b')) return 'BUY';
            if (s.toLowerCase().startsWith('s')) return 'SELL';
        }
        if (s === 1 || s === '1') return 'BUY';
        if (s === 2 || s === -1 || s === '2') return 'SELL';
        return String(s || 'N/A').toUpperCase();
    };

    const getLotSize = (symbol) => {
        const s = symbol.toUpperCase();
        if (s.includes('BANKNIFTY')) return LOT_SIZES['BANKNIFTY'];
        if (s.includes('FINNIFTY')) return LOT_SIZES['FINNIFTY'];
        if (s.includes('NIFTY')) return LOT_SIZES['NIFTY'];
        if (s.includes('SENSEX')) return LOT_SIZES['SENSEX'];
        if (s.includes('MIDCAP')) return LOT_SIZES['MIDCAP'];
        return 1; // Default to 1 if not found, avoids division by zero
    };

    const handleApplyPercentage = () => {
        const pct = parseFloat(percentage);
        if (!pct || isNaN(pct) || pct <= 0 || pct > 100) return;

        const newOverrides = {};
        positions.forEach(p => {
            const key = getRowKey(p);
            const originalQty = getClientData(p).qty;
            const lotSize = getLotSize(p.symbol);

            // Formula: (round((total qty * x% ) / lot_size)) * lot_size
            const calculated = Math.round((originalQty * (pct / 100)) / lotSize) * lotSize;

            // Ensure at least 1 lot if calculated is 0 but original wasn't 0? 
            // Or just follow formula exactly. 
            // If 10% of 1 lot (e.g. 65), (6.5/65) = 0.1 -> round(0.1) = 0 -> 0. 
            // Maybe safer to clamp to at least lot_size if original > 0?
            // Actually user just said "calculate qty % wise", I will follow formula.
            // If result is 0, let's keep it 0 or maybe set to lot_size?
            // Usually 0 suggests exit nothing.

            newOverrides[key] = Math.max(0, calculated);
        });
        setQtyOverrides(prev => ({ ...prev, ...newOverrides }));
    };

    // On Confirm, we pass back the map of { rowKey: finalQty }
    // or maybe better: pass back the *full* payload? 
    // Let's pass back the QtyMap, let the parent reconstruct the payload to keep this component dumb-ish.
    const handleConfirmClick = () => {
        // Prepare the map of ALL final quantities (default or overridden)
        const finalQtys = {};
        const finalTypes = {};
        positions.forEach(p => {
            const key = getRowKey(p);
            finalQtys[key] = getDisplayQty(p);
            finalTypes[key] = getDisplayType(p);
        });
        onConfirm(finalQtys, finalTypes);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-[#14161f] rounded-lg w-[90%] max-w-4xl border border-red-500/30 shadow-xl">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                    <div className="flex items-center gap-2 text-red-400 font-bold text-sm">
                        <AlertTriangle className="w-5 h-5" />
                        Confirm Exit Selected Positions
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
                </div>

                <div className="px-6 py-2 text-[11px] text-gray-400 flex justify-between items-center">
                    <span>Selected Orders: <b className="text-red-400">{positions.length}</b></span>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-400">Exit %:</span>
                        <input
                            type="number"
                            className="bg-gray-800 border border-gray-600 rounded w-16 px-2 py-1 text-right text-white focus:border-blue-500 outline-none"
                            placeholder="%"
                            value={percentage}
                            onChange={(e) => setPercentage(e.target.value)}
                        />
                        <button
                            onClick={handleApplyPercentage}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs transition"
                        >
                            Apply
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="px-4 pb-4 max-h-[60vh] overflow-auto">
                    <table className="w-full text-xs text-left">
                        <thead className="sticky top-0 bg-[#14161f] z-10">
                            <tr className="text-gray-400 border-b border-gray-700">
                                <th className="px-2 py-2 w-10">#</th>
                                <th className="px-3 py-2">Client</th>
                                <th className="px-3 py-2">Broker</th>
                                <th className="px-3 py-2">Symbol</th>
                                <th className="px-3 py-2">Strategy</th>
                                <th className="px-3 py-2">Side</th>
                                <th className="px-3 py-2">Type</th>
                                <th className="px-3 py-2 text-right">Qty</th>
                                <th className="px-3 py-2 text-center w-14">Remove</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {positions.map((p, i) => {
                                const { clientId, symbol, strategy, side } = getClientData(p);
                                const currentQty = getDisplayQty(p);
                                const currentType = getDisplayType(p);
                                const broker = p?.broker || "—";
                                return (
                                    <tr key={getRowKey(p)} className="text-gray-300 hover:bg-white/5">
                                        <td className="px-2 py-2 text-gray-500 font-mono">{i + 1}</td>
                                        <td className="px-3 py-2">{clientId}</td>
                                        <td className="px-3 py-2">{broker}</td>
                                        <td className="px-3 py-2 font-mono">{symbol}</td>
                                        <td className="px-3 py-2">{strategy}</td>
                                        <td className={`px-2 py-3 font-bold ${normalizeSide(side) === "BUY" ? "text-green-400" : "text-red-400"}`}>
                                            {normalizeSide(side)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <select
                                                className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white focus:border-blue-500 outline-none"
                                                value={currentType}
                                                onChange={(e) => handleTypeChange(p, e.target.value)}
                                            >
                                                <option value={1}>LIMIT</option>
                                                <option value={2}>MARKET</option>
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono">
                                            <input
                                                type="number"
                                                className="bg-gray-800 border border-gray-600 rounded w-20 px-2 py-1 text-right text-white focus:border-blue-500 outline-none"
                                                value={currentQty}
                                                onChange={(e) => handleQtyChange(p, e.target.value)}
                                                min="1"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                onClick={() => onRemove(p)}
                                                className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 transition"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {positions.length === 0 && (
                                <tr><td colSpan={8} className="py-6 text-center text-gray-500 text-sm">No orders selected for exit</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700">
                    <button onClick={onClose} className="px-4 py-2 text-xs rounded bg-gray-700 text-gray-200 hover:bg-gray-600">Cancel</button>
                    <button
                        disabled={positions.length === 0 || isLoading}
                        onClick={handleConfirmClick}
                        className={`px-4 py-2 text-xs rounded text-white flex items-center justify-center gap-2 ${positions.length === 0 || isLoading ? "bg-gray-600 cursor-not-allowed" : "bg-red-600 hover:bg-red-500 animate-pulse"
                            }`}
                    >
                        {isLoading ? "Exiting..." : "Confirm Exit"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExitPayloadModal;
